import React from "react";
import { Helmet } from "react-helmet-async";
import { RESTAURANT } from "@/lib/restaurant";

const BASE_URL = "https://dancing-trappes.preview.emergentagent.com";
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_dancing-trappes/artifacts/xluqcq33_WhatsApp%20Image%202026-04-26%20at%2022.44.56.jpeg";

/**
 * Per-page SEO + GEO + Open Graph + JSON-LD
 *
 * @param {string} title - Page title (will be appended with restaurant name)
 * @param {string} description - Meta description
 * @param {string} path - URL path of the page (e.g. "/menu")
 * @param {string} image - OG image (optional)
 * @param {object} jsonLd - Additional structured data (optional)
 */
export default function SEO({ title, description, path = "/", image, jsonLd }) {
  const fullTitle = title
    ? `${title} | ${RESTAURANT.name} — Restaurant portugais à Trappes`
    : `${RESTAURANT.name} — Restaurant portugais à Trappes (78190) | Plats du jour & soirées dansantes`;
  const desc = description || RESTAURANT.description;
  const url = `${BASE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {/* GEO tags */}
      <meta name="geo.region" content="FR-78" />
      <meta name="geo.placename" content="Trappes, Yvelines" />
      <meta name="geo.position" content={`${RESTAURANT.geo.lat};${RESTAURANT.geo.lng}`} />
      <meta name="ICBM" content={`${RESTAURANT.geo.lat}, ${RESTAURANT.geo.lng}`} />

      {/* Open Graph */}
      <meta property="og:type" content="restaurant.restaurant" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content={RESTAURANT.name} />
      <meta property="restaurant:contact_info:phone_number" content={RESTAURANT.phone} />
      <meta property="restaurant:contact_info:street_address" content={RESTAURANT.fullAddress} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="googlebot" content="index, follow" />

      {/* Page-specific JSON-LD */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

/**
 * Site-wide JSON-LD (Restaurant schema) — placed once in App.js
 */
export function GlobalSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": `${BASE_URL}/#restaurant`,
    name: RESTAURANT.name,
    legalName: RESTAURANT.legalName,
    description: RESTAURANT.description,
    image: [DEFAULT_OG_IMAGE],
    url: BASE_URL,
    telephone: RESTAURANT.phone,
    email: RESTAURANT.email,
    priceRange: "€€",
    servesCuisine: ["Portuguese", "Cuisine portugaise", "Restauration traditionnelle"],
    address: {
      "@type": "PostalAddress",
      streetAddress: RESTAURANT.address,
      postalCode: RESTAURANT.postcode,
      addressLocality: RESTAURANT.city,
      addressRegion: RESTAURANT.region,
      addressCountry: "FR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: RESTAURANT.geo.lat,
      longitude: RESTAURANT.geo.lng,
    },
    openingHoursSpecification: RESTAURANT.openingHoursSpec.map((s) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: s.days,
      opens: s.opens,
      closes: s.closes,
    })),
    acceptsReservations: "True",
    hasMenu: `${BASE_URL}/menu`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: RESTAURANT.google.rating,
      reviewCount: RESTAURANT.google.reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
    sameAs: [],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export const SEO_BASE_URL = BASE_URL;
